import { inject } from 'inversify';

import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Post } from '@/entities/Post';
import { Logger } from '@/services/Logger';
import { PostStore } from '@/stores/PostStore';
import { messageOrderComparision } from '@/utils/messageOrderComparision';
import { orderByField } from '@/utils/orderBy';
import type { IPostInfo } from '~/api/IPostInfo';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';

@SignalrListener
@service(PostManager)
export class PostManager {
  @inject(Logger) protected logger: Logger;
  @inject(PostStore) protected postStore: PostStore;

  getDropdownOptions() {
    return orderByField(this.postStore.dropdownOptions(), 'label');
  }

  get getPosts() {
    const posts = this.postStore.all().filter((post) => post.status === '1');
    return orderByField(posts, this.postStore.valueKey);
  }

  find(id: string) {
    return this.postStore.find(id);
  }

  update(posts: Post[]) {
    posts.forEach((post) => {
      const normalizedPost = new Post(post);
      this.postStore.update(normalizedPost);
    });
  }

  @SignalrMessage('PostUpdateMsg')
  async onPostUpdateMsg(post: IPostInfo & IDispatchMessage) {
    const { sentTime } = post;
    const existingPost = this.find(post.id);

    if (messageOrderComparision(sentTime, existingPost?.sentTime)) {
      this.update([{ ...post, sentTime }]);
    }
    else {
      this.logger.captureMessage('Dropped message', { tags: { store: 'post', action: 'update' } });
    }
  }
}
